<template>
  <div>
    <div class="table-tools">
      <h2 class="page-title" style="margin-bottom:0;">供应商信息</h2>
      <el-button @click="showPasswordDialog = true">修改密码</el-button>
    </div>

    <div class="card-panel">
      <h3 class="section-title">基础信息</h3>
      <p class="section-desc">查看当前供应商账号、主体类型和权限状态。</p>
      <div class="info-grid">
        <div class="info-item">
          <div class="info-label">供应商名称</div>
          <div class="info-value">{{ displayValue(profile.name) }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">登录编号</div>
          <div class="info-value code-text">{{ displayValue(profile.vendorCode) }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">短编号</div>
          <div class="info-value code-text">{{ displayValue(profile.shortCode) }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">供应商类型</div>
          <div class="info-value">{{ displayValue(profile.supplierTypeText) }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">状态</div>
          <div class="info-value">{{ profile.isActive ? '启用' : '停用' }}</div>
        </div>
        <div class="info-item">
          <div class="info-label">特殊权限</div>
          <div class="info-value">{{ specialPermissionText }}</div>
        </div>
      </div>
    </div>

    <div class="card-panel">
      <h3 class="section-title">{{ supplierProfileTitle }}</h3>
      <p class="section-desc">主体资料由管理后台维护，供应商后台仅提供只读核对。</p>
      <div class="info-grid" v-if="supplierProfileItems.length">
        <div class="info-item" v-for="item in supplierProfileItems" :key="item.label">
          <div class="info-label">{{ item.label }}</div>
          <div class="info-value" :class="{ 'code-text': item.code }">{{ displayValue(item.value) }}</div>
        </div>
      </div>
      <div v-else class="empty-panel">暂无主体资料</div>
    </div>

    <div class="card-panel">
      <h3 class="section-title">名片展示资料</h3>
      <p class="section-desc">头像/Logo 使用本地上传文件；作者主页会作为公开名片信息展示，请填写 http:// 或 https:// 开头的地址。</p>
      <el-form :model="profileForm" label-position="top">
        <el-form-item label="头像/Logo">
          <div class="avatar-upload-row">
            <img v-if="avatarPreview" :src="avatarPreview" class="avatar-preview" alt="头像/Logo" />
            <el-upload :show-file-list="false" :http-request="uploadAvatar" accept="image/png,image/jpeg,image/webp">
              <el-button>上传头像/Logo</el-button>
            </el-upload>
            <el-button v-if="profileForm.avatarUrl" type="text" @click="clearAvatar">移除头像</el-button>
          </div>
        </el-form-item>
        <el-form-item label="作者主页URL">
          <el-input v-model="profileForm.authorHomeUrl" placeholder="例如 https://example.com/author" />
        </el-form-item>
        <div class="action-row">
          <el-button type="primary" @click="saveProfile">保存名片展示资料</el-button>
          <el-button @click="openCard">查看公开名片</el-button>
        </div>
      </el-form>
    </div>

    <div class="card-panel">
      <h3 class="section-title">分配给我的号段</h3>
      <p class="section-desc">查看当前供应商可使用的 SN段1 和剩余容量。</p>
      <div v-if="profile.segments && profile.segments.length" class="table-scroll">
        <el-table :data="profile.segments" width="100%">
          <el-table-column prop="segmentCode" label="SN段1" width="100" />
          <el-table-column prop="productName" label="产品名" />
          <el-table-column prop="launchDate" label="计划投放日期" />
          <el-table-column prop="launchRegion" label="计划投放地区" />
          <el-table-column prop="status" label="状态" width="100" />
          <el-table-column prop="remainingCount" label="剩余数量" width="120" />
        </el-table>
      </div>
      <div v-else class="empty-panel">暂无分配号段</div>
    </div>

    <el-dialog title="修改供应商密码" :visible.sync="showPasswordDialog" width="420px">
      <el-form :model="passwordForm" label-position="top">
        <el-form-item label="原密码">
          <el-input v-model="passwordForm.oldPassword" type="password" show-password />
        </el-form-item>
        <el-form-item label="新密码">
          <el-input v-model="passwordForm.newPassword" type="password" show-password />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showPasswordDialog = false">取消</el-button>
        <el-button type="primary" @click="submitPassword">确认修改</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { avatarAssetUrl, uploadVendorAvatar } from '../../api/avatar'
import { changeVendorPassword, getVendorProfile, updateVendorProfile } from '../../api/vendor'

export default {
  data() {
    return {
      profile: {
        name: '',
        vendorCode: '',
        shortCode: '',
        supplierType: 'individual',
        supplierTypeText: '个人',
        isActive: true,
        authorHomeUrl: '',
        contactProfile: {},
        companyProfile: {},
        qualificationProfile: {},
        segments: [],
      },
      profileForm: {
        authorHomeUrl: '',
        avatarUrl: '',
      },
      showPasswordDialog: false,
      passwordForm: {
        oldPassword: '',
        newPassword: '',
      },
    }
  },
  computed: {
    specialPermissionText() {
      const permissions = Array.isArray(this.profile.specialPermissions) ? this.profile.specialPermissions : []
      return permissions.length ? permissions.join('、') : '无'
    },
    supplierProfileTitle() {
      return `${this.profile.supplierTypeText || '供应商'}资料`
    },
    avatarPreview() {
      return avatarAssetUrl(this.profileForm.avatarUrl || this.profile.avatarUrl)
    },
    supplierProfileItems() {
      const contact = this.profile.contactProfile || {}
      const company = this.profile.companyProfile || {}
      const qualification = this.profile.qualificationProfile || {}
      if (this.profile.supplierType === 'circle') {
        return [
          { label: '社团名称', value: company.companyName },
          { label: '主理人', value: contact.contactName },
          { label: '联系方式', value: contact.contactMethod },
          { label: '社团平台/主页', value: company.businessScope, code: true },
          { label: '社团简介', value: company.description },
        ]
      }
      if (this.profile.supplierType === 'certified_company') {
        return [
          { label: '企业名称', value: company.companyName },
          { label: '统一社会信用代码', value: company.unifiedSocialCreditCode, code: true },
          { label: '法定代表人', value: company.legalRepresentative },
          { label: '注册地址', value: company.registeredAddress },
          { label: '经营范围', value: company.businessScope },
          { label: '营业执照编号', value: qualification.licenseNo, code: true },
          { label: '认证状态', value: this.qualificationStatusText(qualification.status) },
        ]
      }
      return [
        { label: '联系人姓名', value: contact.contactName },
        { label: '联系方式', value: contact.contactMethod },
        { label: '联系邮箱', value: contact.contactEmail, code: true },
        { label: '联系地址', value: contact.contactAddress },
      ]
    },
  },
  created() {
    document.title = 'CoRecogN'
    this.loadProfile()
  },
  methods: {
    displayValue(value) {
      return value || '-'
    },
    qualificationStatusText(status) {
      return {
        pending: '待认证',
        approved: '已认证',
        rejected: '未通过',
      }[status] || '-'
    },
    async loadProfile() {
      const { data } = await getVendorProfile()
      this.profile = data
      this.profileForm.authorHomeUrl = data.authorHomeUrl || ''
      this.profileForm.avatarUrl = data.avatarUrl || ''
    },
    async saveProfile() {
      try {
        await updateVendorProfile(this.profileForm)
        this.$message.success('供应商信息已保存')
        this.loadProfile()
      } catch (error) {
        this.$message.error(error.response?.data?.message || '保存失败')
      }
    },
    async uploadAvatar(option) {
      try {
        const { data } = await uploadVendorAvatar(option.file)
        this.profileForm.avatarUrl = data.avatarUrl
        this.$message.success('头像已上传，请保存名片展示资料')
      } catch (error) {
        this.$message.error(error.response?.data?.message || '上传失败')
      }
    },
    clearAvatar() {
      this.profileForm.avatarUrl = ''
    },
    openCard() {
      if (this.profile.vendorCode) window.open(`/#/card/${this.profile.vendorCode}`, '_blank', 'noopener,noreferrer')
    },
    async submitPassword() {
      try {
        await changeVendorPassword(this.passwordForm)
        this.$message.success('密码修改成功')
        this.passwordForm = { oldPassword: '', newPassword: '' }
        this.showPasswordDialog = false
      } catch (error) {
        this.$message.error(error.response?.data?.message || '修改失败')
      }
    },
  },
}
</script>

<style scoped>
.avatar-upload-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.avatar-preview {
  width: 72px;
  height: 72px;
  border-radius: 18px;
  object-fit: cover;
  border: 1px solid #e5e7eb;
}
</style>
